import { Button, FormField, Modal, Select } from '@dev-spendesk/grapes';
import { useFormik } from 'formik';
import { useMemo } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';

import {
  convertToDate,
  getBudgetaryExerciseYears,
  getMonthIndex,
  getTranslatedMonthOptions,
} from './dates';
import { type FormState, validate } from './form';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  maxRange: { from: Date; to: Date };
  onChange: ({ from, to }: { from: Date; to: Date }) => void;
  selectedDateRange: { from: Date; to: Date };
};

const CustomPeriodModal = ({
  isOpen,
  onClose,
  maxRange,
  onChange,
  selectedDateRange,
}: Props) => {
  const { t } = useTranslation('global');
  const formikProps = useFormik<FormState>({
    initialValues: {
      fromMonthIndex: selectedDateRange
        ? getMonthIndex(selectedDateRange.from)
        : undefined,
      fromYear: getBudgetaryExerciseYears(maxRange)[0],
      toMonthIndex: selectedDateRange
        ? getMonthIndex(selectedDateRange.to)
        : undefined,
      toYear:
        getBudgetaryExerciseYears(maxRange)[
          getBudgetaryExerciseYears(maxRange).length - 1
        ],
    },
    validateOnChange: false,
    validate: (values) => validate(values, t, maxRange),
    onSubmit: async (values) => {
      let { fromMonthIndex, toMonthIndex } = values;
      if (
        values.fromMonthIndex === undefined ||
        values.toMonthIndex === undefined
      ) {
        // if only one of the values selected, consider it as one month
        fromMonthIndex = values.fromMonthIndex ?? values.toMonthIndex;
        toMonthIndex = values.toMonthIndex ?? values.fromMonthIndex;
      }
      if (values.fromYear && values.toYear && fromMonthIndex && toMonthIndex) {
        onChange({
          from: convertToDate(fromMonthIndex, values.fromYear),
          to: convertToDate(toMonthIndex, values.toYear),
        });
      }
    },
  });

  const monthOptions = useMemo(
    () =>
      [...Array(12).keys()].map((_, index) =>
        getTranslatedMonthOptions(index, t),
      ),
    [selectedDateRange],
  );

  const yearOptions = useMemo(() => {
    return getBudgetaryExerciseYears(maxRange).map((year) => ({
      key: year,
      label: year,
    }));
  }, [selectedDateRange]);
  return (
    <Modal
      iconName="calendar"
      iconVariant="purple"
      title={t('budget.breakdownSpendings.selectCustomPeriod')}
      isOpen={isOpen}
      onClose={onClose}
      actions={[
        <Button
          text={t('misc.cancel')}
          variant="secondaryNeutral"
          onClick={onClose}
          key="cancel"
        />,
        <Button
          key="apply"
          variant="primaryBrand"
          text={t('budget.breakdownSpendings.apply')}
          onClick={() => formikProps.handleSubmit()}
        />,
      ]}
    >
      <div className="mt-8 mb-16">
        <div className="flex w-full gap-16">
          <FormField
            label={t('misc.from')}
            className="w-full text-left"
            alertMessage={formikProps.errors.fromMonthIndex}
          >
            <Select
              onSelect={(option) => {
                formikProps.setFieldValue('fromMonthIndex', option.key);
                // Automatically set 'to' month to the same month for easier single-month selection
                if (formikProps.values.toMonthIndex === undefined) {
                  formikProps.setFieldValue('toMonthIndex', option.key);
                }
              }}
              fit="parent"
              value={
                formikProps.values.fromMonthIndex !== undefined
                  ? getTranslatedMonthOptions(
                      formikProps.values.fromMonthIndex,
                      t,
                    )
                  : undefined
              }
              options={monthOptions}
              placeholder={t('budget.breakdownSpendings.monthPlaceholder')}
            />
          </FormField>
          <FormField
            label=""
            alertMessage={formikProps.errors.fromYear}
            className="w-full text-left"
          >
            <Select
              placeholder={t('budget.breakdownSpendings.yearPlaceholder')}
              onSelect={(option) => {
                formikProps.setFieldValue('fromYear', option.key);
                // Automatically set 'to' year to the same year for easier single-month selection
                if (
                  formikProps.values.toYear === undefined ||
                  formikProps.values.toMonthIndex === undefined
                ) {
                  formikProps.setFieldValue('toYear', option.key);
                }
              }}
              fit="parent"
              options={yearOptions}
              className="mt-4"
              isDisabled={getBudgetaryExerciseYears(maxRange).length === 1}
              value={
                formikProps.values.fromYear
                  ? {
                      key: formikProps.values.fromYear,
                      label: formikProps.values.fromYear,
                    }
                  : undefined
              }
            />
          </FormField>
        </div>
      </div>
      <div className="mb-16 flex w-full gap-16">
        <FormField
          label={t('misc.to')}
          alertMessage={formikProps.errors.toMonthIndex}
          className="w-full text-left"
        >
          <Select
            fit="parent"
            onSelect={(option) => {
              formikProps.setFieldValue('toMonthIndex', option.key);
            }}
            value={
              formikProps.values.toMonthIndex !== undefined
                ? getTranslatedMonthOptions(formikProps.values.toMonthIndex, t)
                : undefined
            }
            options={monthOptions}
            placeholder={t('budget.breakdownSpendings.monthPlaceholder')}
          />
        </FormField>
        <FormField
          label=""
          alertMessage={formikProps.errors.toYear}
          className="w-full text-left"
        >
          <Select
            placeholder={t('budget.breakdownSpendings.yearPlaceholder')}
            onSelect={(option) => {
              formikProps.setFieldValue('toYear', option.key);
            }}
            fit="parent"
            className="mt-4"
            options={yearOptions}
            isDisabled={getBudgetaryExerciseYears(maxRange).length === 1}
            value={
              formikProps.values.toYear
                ? {
                    key: formikProps.values.toYear,
                    label: formikProps.values.toYear,
                  }
                : undefined
            }
          />
        </FormField>
      </div>
    </Modal>
  );
};

export default CustomPeriodModal;
